import { onMount, onDestroy } from "@/modules/_";
import { handleEditor } from "@webflow/detect-editor";

const IDLE_HIDE_MS = 2500;

// Title marquee: only kicks in when the title is too wide to fit. Speed is in
// px/sec (kept low for a slow drift); GAP is the space between the looped
// copies; FADE is the width of the gradient that dissolves each edge.
const MARQUEE_SPEED = 30;
const MARQUEE_GAP = 12;
const MARQUEE_FADE = 24;

// Tooltip velocity tilt. While the pointer moves along the timeline the
// tooltip leans into the direction of travel, by an amount that scales with
// speed. A rAF loop drives it: each move feeds in an instantaneous velocity,
// the velocity decays toward 0 when the pointer stops, and the rendered angle
// eases toward velocity * FACTOR — so the whole thing reads as one smooth,
// subtle spring rather than discrete jumps.
//
// FACTOR maps pointer velocity (px/ms) to degrees. MAX_TILT caps the lean.
// VELOCITY_DECAY (0–1, per frame) bleeds off speed once movement stops, so the
// tooltip drifts back upright. EASE (0–1, per frame) is how quickly the
// rendered angle chases its target — lower is smoother/lazier.
const TOOLTIP_TILT_FACTOR = 11;
const TOOLTIP_MAX_TILT = 18;
const TOOLTIP_TILT_VELOCITY_DECAY = 0.82;
const TOOLTIP_TILT_EASE = 0.12;
// The tooltip sits slightly shrunk while hidden and springs to full size on
// hover, overshooting past full size to POP_SCALE before settling at 1 — that
// overshoot is the "pop". POP_MS is the length of that one-shot animation.
const TOOLTIP_HIDDEN_SCALE = 0.9;
const TOOLTIP_POP_SCALE = 1.07;
const TOOLTIP_POP_MS = 480;

type Role =
  | "element"
  | "poster"
  | "controls"
  | "play"
  | "play-icon"
  | "pause-icon"
  | "mute"
  | "volume-on"
  | "volume-off"
  | "volume"
  | "volume-fill"
  | "volume-handle"
  | "volume-wrapper"
  | "volume-clip"
  | "time-current"
  | "time-total"
  | "progress"
  | "progress-buffer"
  | "progress-fill"
  | "progress-handle"
  | "tooltip"
  | "title"
  | "fullscreen"
  | "expand"
  | "fullscreen-enter"
  | "fullscreen-exit"
  | "settings"
  | "settings-menu"
  | "height";

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export default function (element: HTMLElement) {
  if (handleEditor()) return;

  const q = <T extends HTMLElement = HTMLElement>(role: Role) =>
    element.querySelector<T>(`[data-video="${role}"]`);

  // Any role can legitimately appear more than once: a play button on the
  // poster *and* a play/pause toggle in the bar, or a whole second controls
  // bar for the expanded/fullscreen view. querySelector only returns the
  // first, so every control here is collected as an array and wired up for
  // each copy — indicators stay in sync, and triggers respond, no matter
  // which duplicate the user touched.
  const qa = <T extends HTMLElement = HTMLElement>(role: Role) =>
    Array.from(element.querySelectorAll<T>(`[data-video="${role}"]`));

  const video = q<HTMLVideoElement>("element");
  if (!video) return;

  const posters = qa("poster");
  const controlsBars = qa("controls");
  const playBtns = qa("play");
  const playIcons = qa("play-icon");
  const pauseIcons = qa("pause-icon");
  const muteBtns = qa("mute");
  const volumeOns = qa("volume-on");
  const volumeOffs = qa("volume-off");
  const volumeTracks = qa("volume");
  const volumeFills = qa("volume-fill");
  const volumeHandles = qa("volume-handle");
  const volumeWrappers = qa("volume-wrapper");
  const timeCurrents = qa("time-current");
  const timeTotals = qa("time-total");
  const progressTracks = qa("progress");
  const progressBuffers = qa("progress-buffer");
  const progressFills = qa("progress-fill");
  const progressHandles = qa("progress-handle");
  // A hover tooltip that tracks the pointer along the timeline and shows the
  // time at the hovered position. Lives inside its progress track (so a left
  // percentage positions it correctly); a global copy is used as a fallback.
  const tooltips = qa("tooltip");
  // The title is duplicated in the markup (one copy per breakpoint, toggled
  // via display), so each needs its own marquee evaluation. A hidden copy
  // measures 0 width and simply skips setup until a resize makes it visible.
  const titles = qa("title");
  // "fullscreen" and "expand" are aliases for the same toggle.
  const fullscreenBtns = [...qa("fullscreen"), ...qa("expand")];
  const fullscreenEnters = qa("fullscreen-enter");
  const fullscreenExits = qa("fullscreen-exit");
  const settingsBtns = qa("settings");
  const settingsMenu = q("settings-menu");
  // Spacer(s) whose top padding is kept equal to the video's rendered height —
  // e.g. to reserve room for an absolutely-positioned / sticky video above
  // following content.
  const heightEls = qa("height");

  const ac = new AbortController();
  const { signal } = ac;
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  // At the 991px breakpoint and below, playback should go fullscreen.
  const mobileQuery = window.matchMedia("(max-width: 991px)");

  let scrubbing = false;
  let volScrubbing = false;
  let idleTimer: number | null = null;
  let titleCleanups: Array<() => void> = [];
  let titleResizeTimer: number | null = null;
  // Controls start visible; tracked so the pointermove path can skip redundant
  // style writes.
  let controlsVisible = true;
  let lastCurrentText = "";

  const togglePlay = () => {
    if (video!.paused) video!.play().catch(() => {});
    else video!.pause();
  };

  function setIconsVisible(els: HTMLElement[], visible: boolean) {
    for (const el of els) el.style.display = visible ? "" : "none";
  }

  function syncPlayIcons() {
    const paused = video!.paused;
    setIconsVisible(playIcons, paused);
    setIconsVisible(pauseIcons, !paused);
  }

  function syncMuteIcons() {
    const muted = video!.muted || video!.volume === 0;
    setIconsVisible(volumeOns, !muted);
    setIconsVisible(volumeOffs, muted);
  }

  function applyVolumeUI(pct: number) {
    for (const el of volumeFills) el.style.width = `${pct}%`;
    for (const el of volumeHandles) el.style.left = `${pct}%`;
  }

  function syncVolume() {
    const level = video!.muted ? 0 : video!.volume;
    applyVolumeUI(level * 100);
  }

  function setVolumeFromEvent(e: PointerEvent, rect: DOMRect) {
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    video!.volume = ratio;
    video!.muted = ratio === 0;
    applyVolumeUI(ratio * 100);
  }

  // On mobile breakpoints, take the player fullscreen on play. The desktop
  // Fullscreen API is requested on the container so our custom controls come
  // along; iOS Safari has no element-level fullscreen, so fall back to the
  // video's native fullscreen there. Called from the "play" event, which fires
  // off a user gesture (tapping play), keeping us inside transient activation.
  function enterFullscreenIfMobile() {
    if (!mobileQuery.matches) return;
    if (document.fullscreenElement === element) return;
    if (element.requestFullscreen) {
      element.requestFullscreen().catch(() => {});
    } else {
      const iosFs = (video as HTMLVideoElement & {
        webkitEnterFullscreen?: () => void;
      }).webkitEnterFullscreen;
      if (typeof iosFs === "function") {
        try {
          iosFs.call(video);
        } catch {}
      }
    }
  }

  function syncFullscreenIcons() {
    const isFs = document.fullscreenElement === element;
    setIconsVisible(fullscreenEnters, !isFs);
    setIconsVisible(fullscreenExits, isFs);
  }

  // Leave fullscreen if we're in it — covers both the element-level Fullscreen
  // API (desktop) and the video's native fullscreen (iOS Safari), mirroring the
  // two paths in enterFullscreenIfMobile.
  function exitFullscreen() {
    if (document.fullscreenElement === element) {
      document.exitFullscreen().catch(() => {});
    }
    const iosExit = (video as HTMLVideoElement & {
      webkitExitFullscreen?: () => void;
      webkitDisplayingFullscreen?: boolean;
    });
    if (iosExit.webkitDisplayingFullscreen && typeof iosExit.webkitExitFullscreen === "function") {
      try {
        iosExit.webkitExitFullscreen();
      } catch {}
    }
  }

  function applyProgressUI(pct: number) {
    for (const el of progressFills) el.style.width = `${pct}%`;
    for (const el of progressHandles) el.style.left = `${pct}%`;
  }

  function syncProgress() {
    const { currentTime, duration } = video!;
    const text = formatTime(currentTime);
    if (text !== lastCurrentText) {
      for (const el of timeCurrents) el.textContent = text;
      lastCurrentText = text;
    }
    if (!duration || !Number.isFinite(duration)) return;
    applyProgressUI((currentTime / duration) * 100);
  }

  function syncDuration() {
    const text = formatTime(video!.duration);
    for (const el of timeTotals) el.textContent = text;
  }

  // Width the buffer bar(s) to the loaded-ahead range. video.buffered can hold
  // several disjoint ranges (after a seek), so use the one containing the
  // playhead and paint up to its end — that's the contiguous run that will play
  // without stalling. No matching range (e.g. a fresh seek into an unbuffered
  // gap) collapses the bar to 0.
  function syncBuffer() {
    if (!progressBuffers.length) return;
    const { buffered, currentTime, duration } = video!;
    if (!duration || !Number.isFinite(duration) || !buffered.length) return;
    let end = currentTime;
    for (let i = 0; i < buffered.length; i++) {
      if (buffered.start(i) <= currentTime && currentTime <= buffered.end(i)) {
        end = buffered.end(i);
        break;
      }
    }
    const pct = Math.min(100, Math.max(0, (end / duration) * 100));
    for (const el of progressBuffers) el.style.width = `${pct}%`;
  }

  // Set each [data-video="height"] spacer's top padding to the video's aspect
  // ratio as a percentage. Percentage padding resolves against the containing
  // block's width, so this reserves the right height and holds the ratio
  // automatically as the layout resizes — no per-frame measurement, and immune
  // to the customer card's playback scale (which a pixel height would fold in).
  // videoWidth/Height are 0 until metadata loads, so this no-ops until then.
  function syncHeight() {
    if (!heightEls.length) return;
    const { videoWidth, videoHeight } = video!;
    if (!videoWidth || !videoHeight) return;
    const pct = (videoHeight / videoWidth) * 100;
    for (const el of heightEls) el.style.paddingTop = `${pct}%`;
  }

  function seekFromEvent(e: PointerEvent, rect: DOMRect) {
    if (!video!.duration || !Number.isFinite(video!.duration)) return;
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    video!.currentTime = ratio * video!.duration;
    applyProgressUI(ratio * 100);
  }

  function setPosterVisible(visible: boolean) {
    for (const p of posters) p.style.display = visible ? "flex" : "none";
  }

  function setControlsVisible(visible: boolean) {
    if (visible === controlsVisible) return;
    controlsVisible = visible;
    for (const c of controlsBars) {
      c.style.opacity = visible ? "1" : "0";
      c.style.pointerEvents = visible ? "" : "none";
    }
  }

  function bumpIdle() {
    setControlsVisible(true);
    if (idleTimer !== null) window.clearTimeout(idleTimer);
    if (video!.paused) return;
    idleTimer = window.setTimeout(() => setControlsVisible(false), IDLE_HIDE_MS);
  }

  function setSettingsOpen(open: boolean) {
    if (!settingsMenu) return;
    settingsMenu.style.display = open ? "" : "none";
    for (const b of settingsBtns) b.setAttribute("aria-expanded", String(open));
  }

  function teardownTitleMarquee() {
    for (const cleanup of titleCleanups) cleanup();
    titleCleanups = [];
  }

  // Re-evaluate every title copy from scratch. The titles are duplicated for
  // different breakpoints (toggled via display), so each is measured and
  // animated independently; a hidden copy measures 0 width and is skipped.
  function setupTitleMarquee() {
    teardownTitleMarquee();
    if (prefersReducedMotion.matches) return;
    for (const title of titles) {
      const cleanup = setupTitleMarqueeFor(title);
      if (cleanup) titleCleanups.push(cleanup);
    }
  }

  // If the title is wider than its container, fade both edges with a gradient
  // mask and scroll it as a slow, seamless marquee. Returns a cleanup, or null
  // when the title fits (or is hidden) and no marquee was created.
  function setupTitleMarqueeFor(title: HTMLElement): (() => void) | null {
    // Force a single line so overflow reflects horizontal clipping, then test.
    const prev = {
      whiteSpace: title.style.whiteSpace,
      overflow: title.style.overflow,
      maskImage: title.style.maskImage,
      webkitMaskImage: title.style.getPropertyValue("-webkit-mask-image"),
      paddingLeft: title.style.paddingLeft,
      paddingRight: title.style.paddingRight,
      transform: title.style.transform,
      html: title.innerHTML,
    };
    title.style.whiteSpace = "nowrap";
    title.style.overflow = "hidden";

    if (title.scrollWidth <= title.clientWidth + 1) {
      // Fits (or is a hidden, 0-width copy) — restore the title and bail.
      title.style.whiteSpace = prev.whiteSpace;
      title.style.overflow = prev.overflow;
      return null;
    }

    const text = title.textContent ?? "";

    // Two identical copies in a flex track: translating by exactly one copy's
    // width (text + trailing gap) lands the second where the first began, so
    // the loop is seamless with no visible jump.
    const track = document.createElement("span");
    track.style.display = "inline-flex";
    track.style.flex = "none";
    track.style.willChange = "transform";

    const makeCopy = (hidden: boolean) => {
      const span = document.createElement("span");
      span.textContent = text;
      span.style.flex = "none";
      span.style.paddingRight = `${MARQUEE_GAP}px`;
      // The duplicate is decorative; keep it out of the accessibility tree.
      if (hidden) span.setAttribute("aria-hidden", "true");
      return span;
    };
    const lead = makeCopy(false);
    track.append(lead, makeCopy(true));

    title.innerHTML = "";
    title.append(track);

    // Drop horizontal padding for the duration of the marquee so the overflow
    // clip edge, the mask edge and the text start all coincide at x=0. With
    // padding, `overflow:hidden` clips at the padding box while the mask only
    // covers the content box — leaving an unmasked strip in the padding where
    // text shows through at full opacity (a bright sliver), made obvious by the
    // card's fractional scale. The looped gap handles spacing instead.
    title.style.paddingLeft = "0px";
    title.style.paddingRight = "0px";

    const mask = `linear-gradient(to right, transparent 0, #000 ${MARQUEE_FADE}px, #000 calc(100% - ${MARQUEE_FADE}px), transparent 100%)`;
    title.style.maskImage = mask;
    title.style.setProperty("-webkit-mask-image", mask);

    // Promote the title to its own compositing layer. The track animates on a
    // transform (its own GPU layer); without this, the title's overflow/mask
    // has to clip that separate layer at composite time, and the customer
    // card's fractional scale makes that cross-layer clip leak ~1px of
    // unmasked text past the edge. Owning a layer means the track is clipped
    // *within* the title before the ancestor scale is applied.
    title.style.transform = "translateZ(0)";

    // offsetWidth is the unscaled layout width (text + trailing gap). The
    // translate runs in the track's local space, so the loop distance must be
    // measured before any ancestor scale (the customer card scales while a
    // video plays); getBoundingClientRect would fold that scale in and drift
    // the seam.
    const distance = lead.offsetWidth;
    const duration = Math.max(1, distance / MARQUEE_SPEED);

    const anim = track.animate(
      [{ transform: "translateX(0)" }, { transform: `translateX(-${distance}px)` }],
      { duration: duration * 1000, iterations: Infinity, easing: "linear" }
    );

    return () => {
      anim.cancel();
      title.innerHTML = prev.html;
      title.style.whiteSpace = prev.whiteSpace;
      title.style.overflow = prev.overflow;
      title.style.maskImage = prev.maskImage;
      title.style.setProperty("-webkit-mask-image", prev.webkitMaskImage);
      title.style.paddingLeft = prev.paddingLeft;
      title.style.paddingRight = prev.paddingRight;
      title.style.transform = prev.transform;
    };
  }

  onMount(() => {
    video.controls = false;
    video.playsInline = true;

    // Default the volume to 70% so playback doesn't start at full blast. Only
    // applied when the markup hasn't pinned its own level (volume still at the
    // browser default of 1), so an authored muted-autoplay setup is preserved.
    if (video.volume === 1) video.volume = 0.7;

    for (const c of controlsBars) {
      c.style.transition = "opacity 200ms ease";
    }

    if (settingsMenu) setSettingsOpen(false);

    // Use tabular figures so the time text doesn't reflow as digits change.
    for (const el of [...timeCurrents, ...timeTotals, ...tooltips]) {
      el.style.fontVariantNumeric = "tabular-nums";
    }

    // The tooltip starts hidden and fades in on hover. pointer-events:none keeps
    // it from swallowing the pointermove/scrub events on the track underneath.
    for (const el of tooltips) {
      el.style.opacity = "0";
      el.style.pointerEvents = "none";
      // Pivot the tilt from the bottom-center (where the tooltip points down at
      // the playhead) so it swings like a pendant in the direction of travel,
      // rather than spinning about its middle.
      el.style.transformOrigin = "50% 100%";
      // Start slightly shrunk so the first hover pops it up to full size.
      el.style.transform = `translateX(-50%) rotate(0deg) scale(${TOOLTIP_HIDDEN_SCALE})`;
      // Only opacity is transitioned here: the tilt is driven frame-by-frame by
      // the rAF spring (a CSS transition on transform would fight it), and the
      // pop on show is a separate one-shot animation.
      el.style.transition = "opacity 150ms ease";
    }

    setPosterVisible(video.paused && video.currentTime === 0);

    syncPlayIcons();
    syncMuteIcons();
    syncVolume();
    syncFullscreenIcons();
    syncDuration();
    syncProgress();
    syncBuffer();
    syncHeight();
    setupTitleMarquee();

    // The title's fit depends on available width, so re-evaluate on resize
    // (debounced) — this also covers the width change on fullscreen toggle.
    window.addEventListener("resize", () => {
      if (titleResizeTimer !== null) window.clearTimeout(titleResizeTimer);
      titleResizeTimer = window.setTimeout(setupTitleMarquee, 150);
    }, { signal });

    video.addEventListener("play", () => { syncPlayIcons(); setPosterVisible(false); bumpIdle(); enterFullscreenIfMobile(); }, { signal });
    video.addEventListener("pause", () => { syncPlayIcons(); setControlsVisible(true); }, { signal });
    video.addEventListener("ended", () => { syncPlayIcons(); setPosterVisible(true); setControlsVisible(true); exitFullscreen(); }, { signal });
    video.addEventListener("volumechange", () => { syncMuteIcons(); syncVolume(); }, { signal });
    video.addEventListener("timeupdate", () => { if (!scrubbing) syncProgress(); syncBuffer(); }, { signal });
    video.addEventListener("progress", syncBuffer, { signal });
    video.addEventListener("loadedmetadata", () => { syncDuration(); syncProgress(); syncBuffer(); syncHeight(); }, { signal });
    video.addEventListener("durationchange", syncDuration, { signal });

    for (const btn of playBtns) {
      btn.addEventListener("click", (e) => {
        // Stop the click from also reaching an ancestor handler (e.g. the
        // poster or video toggle), which would re-play right after we pause.
        e.stopPropagation();
        togglePlay();
      }, { signal });
    }

    for (const p of posters) {
      p.addEventListener("click", (e) => {
        e.stopPropagation();
        video.play().catch(() => {});
      }, { signal });
    }

    video.addEventListener("click", togglePlay, { signal });

    for (const btn of muteBtns) {
      btn.addEventListener("click", () => {
        video.muted = !video.muted;
        if (!video.muted && video.volume === 0) video.volume = 1;
      }, { signal });
    }

    // Each progress track keeps its own cached rect in a closure, so seeking on
    // one bar never forces a layout on the other. Only one pointer can scrub at
    // a time, so the shared `scrubbing` flag is enough to gate the move path.
    for (const [i, track] of progressTracks.entries()) {
      track.style.touchAction = "none";
      let rect: DOMRect | null = null;
      // `hovering` tracks whether the pointer is over this track so endScrub
      // can decide whether to leave the tooltip up (mouse still on the bar) or
      // hide it (pointer already gone, e.g. a touch release).
      let hovering = false;
      // Pair the track with the tooltip living inside it, falling back to the
      // matching global copy.
      const tooltip = track.querySelector<HTMLElement>(`[data-video="tooltip"]`) ?? tooltips[i] ?? null;

      const ratioFromEvent = (e: PointerEvent) =>
        Math.min(1, Math.max(0, (e.clientX - rect!.left) / rect!.width));

      // Tilt state. `velocity` is the smoothed pointer speed (px/ms, signed by
      // direction); `tilt` is the rendered angle in degrees, eased toward
      // velocity * FACTOR by the rAF loop below. lastTiltX/T feed the velocity;
      // reset on enter so the first move doesn't read a stale position.
      let lastTiltX: number | null = null;
      let lastTiltT = 0;
      let velocity = 0;
      let tilt = 0;
      let tiltRaf: number | null = null;
      // Full size while shown, shrunk while hidden — so showing pops it up.
      let popped = false;
      let popAnim: Animation | null = null;

      const applyTooltipTransform = () => {
        if (!tooltip) return;
        const scale = popped ? 1 : TOOLTIP_HIDDEN_SCALE;
        tooltip.style.transform = `translateX(-50%) rotate(${tilt}deg) scale(${scale})`;
      };

      // One continuous spring: each frame the velocity bleeds off (so a stopped
      // pointer drifts the tooltip back upright) and the rendered angle eases
      // toward its velocity-derived target. The loop parks itself once settled
      // and is woken again by the next move, so it isn't burning frames while
      // the pointer rests.
      const tiltFrame = () => {
        if (signal.aborted) { tiltRaf = null; return; }
        velocity *= TOOLTIP_TILT_VELOCITY_DECAY;
        const target = Math.max(
          -TOOLTIP_MAX_TILT,
          Math.min(TOOLTIP_MAX_TILT, velocity * TOOLTIP_TILT_FACTOR)
        );
        tilt += (target - tilt) * TOOLTIP_TILT_EASE;
        if (Math.abs(tilt) < 0.02 && Math.abs(target) < 0.02) {
          tilt = 0;
          velocity = 0;
          applyTooltipTransform();
          tiltRaf = null;
          return;
        }
        applyTooltipTransform();
        tiltRaf = requestAnimationFrame(tiltFrame);
      };
      const ensureTiltLoop = () => {
        if (tiltRaf === null && !signal.aborted) tiltRaf = requestAnimationFrame(tiltFrame);
      };
      const stopTiltLoop = () => {
        if (tiltRaf !== null) cancelAnimationFrame(tiltRaf);
        tiltRaf = null;
        velocity = 0;
        tilt = 0;
      };

      // Position the tooltip over the hovered point (centered on the cursor),
      // label it with the time there, and feed the pointer's velocity into the
      // tilt spring.
      const updateTooltip = (e: PointerEvent) => {
        if (!tooltip || !rect) return;
        const ratio = ratioFromEvent(e);
        tooltip.textContent = formatTime(ratio * (video.duration || 0));
        tooltip.style.left = `${ratio * 100}%`;

        const now = performance.now();
        if (lastTiltX !== null) {
          const inst = (e.clientX - lastTiltX) / Math.max(1, now - lastTiltT);
          // Light low-pass so a single jittery reading can't snap the angle.
          velocity += (inst - velocity) * 0.5;
        }
        lastTiltX = e.clientX;
        lastTiltT = now;
        ensureTiltLoop();
      };
      const showTooltip = () => {
        if (!tooltip) return;
        tooltip.style.opacity = "1";
        popped = true;
        applyTooltipTransform();
        // One-shot pop: scale up from hidden size, overshoot past full size,
        // then settle to the resting transform set above. Runs independently
        // of the tilt transition so it stays a clean spring. `left` tracks the
        // pointer throughout since position lives on a separate property.
        popAnim?.cancel();
        popAnim = tooltip.animate(
          [
            { transform: `translateX(-50%) rotate(${tilt}deg) scale(${TOOLTIP_HIDDEN_SCALE})` },
            { transform: `translateX(-50%) rotate(${tilt}deg) scale(${TOOLTIP_POP_SCALE})`, offset: 0.55 },
            { transform: `translateX(-50%) rotate(${tilt}deg) scale(1)` },
          ],
          { duration: TOOLTIP_POP_MS, easing: "ease-out" }
        );
      };
      const hideTooltip = () => {
        if (!tooltip) return;
        popAnim?.cancel();
        popAnim = null;
        stopTiltLoop();
        tooltip.style.opacity = "0";
        popped = false;
        applyTooltipTransform();
      };

      track.addEventListener("pointerenter", () => {
        hovering = true;
        rect = track.getBoundingClientRect();
        lastTiltX = null;
        showTooltip();
      }, { signal });

      track.addEventListener("pointerleave", () => {
        hovering = false;
        // While scrubbing the pointer keeps moving the tooltip even off-track;
        // endScrub will hide it once the gesture finishes.
        if (!scrubbing) {
          hideTooltip();
          rect = null;
        }
      }, { signal });

      track.addEventListener("pointerdown", (e) => {
        scrubbing = true;
        rect = track.getBoundingClientRect();
        track.setPointerCapture(e.pointerId);
        seekFromEvent(e, rect);
        // Touch has no enter event, so reveal the tooltip and reset the tilt
        // baseline on press too.
        lastTiltX = null;
        showTooltip();
        updateTooltip(e);
      }, { signal });

      track.addEventListener("pointermove", (e) => {
        if (!rect) return;
        if (scrubbing) seekFromEvent(e, rect);
        updateTooltip(e);
      }, { signal });

      const endScrub = (e: PointerEvent) => {
        if (!scrubbing) return;
        scrubbing = false;
        if (track.hasPointerCapture(e.pointerId)) {
          track.releasePointerCapture(e.pointerId);
        }
        if (!hovering) {
          hideTooltip();
          rect = null;
        }
      };
      track.addEventListener("pointerup", endScrub, { signal });
      track.addEventListener("pointercancel", endScrub, { signal });
    }

    for (const track of volumeTracks) {
      track.style.touchAction = "none";
      let rect: DOMRect | null = null;

      track.addEventListener("pointerdown", (e) => {
        volScrubbing = true;
        rect = track.getBoundingClientRect();
        track.setPointerCapture(e.pointerId);
        setVolumeFromEvent(e, rect);
      }, { signal });

      track.addEventListener("pointermove", (e) => {
        if (!volScrubbing || !rect) return;
        setVolumeFromEvent(e, rect);
      }, { signal });

      const endVolScrub = (e: PointerEvent) => {
        if (!volScrubbing) return;
        volScrubbing = false;
        rect = null;
        if (track.hasPointerCapture(e.pointerId)) {
          track.releasePointerCapture(e.pointerId);
        }
      };
      track.addEventListener("pointerup", endVolScrub, { signal });
      track.addEventListener("pointercancel", endVolScrub, { signal });
    }

    for (const wrapper of volumeWrappers) {
      // Pair each wrapper with its own clip so two volume controls expand
      // independently. The clip lives inside its wrapper.
      const clip = wrapper.querySelector<HTMLElement>(`[data-video="volume-clip"]`);
      if (!clip) continue;

      // Capture the clip's natural width, then collapse it to 0. Animating to
      // a fixed px width (rather than `auto`) lets the expansion transition.
      const naturalWidth = clip.getBoundingClientRect().width;
      clip.style.overflow = "hidden";
      clip.style.width = "0px";
      // Enable the transition on the next frame so the initial collapse
      // doesn't animate on load. cubic-bezier(0.625,0.05,0,1) is the CSS
      // equivalent of the "osmo" CustomEase (M0,0 C0.625,0.05 0,1 1,1).
      requestAnimationFrame(() => {
        clip.style.transition = "width 550ms cubic-bezier(0.625, 0.05, 0, 1)";
      });

      wrapper.addEventListener("mouseenter", () => {
        const target = naturalWidth || clip.scrollWidth;
        clip.style.width = `${target}px`;
      }, { signal });
      wrapper.addEventListener("mouseleave", () => {
        clip.style.width = "0px";
      }, { signal });
    }

    for (const btn of fullscreenBtns) {
      btn.addEventListener("click", () => {
        if (document.fullscreenElement === element) {
          document.exitFullscreen();
        } else {
          element.requestFullscreen?.();
        }
      }, { signal });
    }

    document.addEventListener("fullscreenchange", () => {
      syncFullscreenIcons();
      setupTitleMarquee();
    }, { signal });

    for (const btn of settingsBtns) {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const open = settingsMenu?.style.display === "none";
        setSettingsOpen(!!open);
      }, { signal });
    }

    document.addEventListener("click", (e) => {
      if (!settingsMenu) return;
      if (settingsMenu.style.display === "none") return;
      const target = e.target as Node;
      if (settingsMenu.contains(target)) return;
      if (settingsBtns.some((b) => b.contains(target))) return;
      setSettingsOpen(false);
    }, { signal });

    element.addEventListener("pointermove", bumpIdle, { signal });
    element.addEventListener("pointerleave", () => {
      if (video.paused) return;
      setControlsVisible(false);
    }, { signal });
    element.addEventListener("focusin", bumpIdle, { signal });
  });

  onDestroy(() => {
    ac.abort();
    teardownTitleMarquee();
    if (titleResizeTimer !== null) {
      window.clearTimeout(titleResizeTimer);
      titleResizeTimer = null;
    }
    if (idleTimer !== null) {
      window.clearTimeout(idleTimer);
      idleTimer = null;
    }
    for (const el of heightEls) el.style.removeProperty("padding-top");
    if (document.fullscreenElement === element) {
      document.exitFullscreen().catch(() => {});
    }
    for (const c of controlsBars) {
      c.style.removeProperty("opacity");
      c.style.removeProperty("pointer-events");
      c.style.removeProperty("transition");
    }
    for (const p of posters) p.style.removeProperty("display");
    for (const el of progressBuffers) el.style.removeProperty("width");
    for (const el of progressFills) el.style.removeProperty("width");
    for (const el of progressHandles) el.style.removeProperty("left");
    for (const el of tooltips) {
      el.style.removeProperty("opacity");
      el.style.removeProperty("pointer-events");
      el.style.removeProperty("transition");
      el.style.removeProperty("left");
      el.style.removeProperty("transform");
      el.style.removeProperty("transform-origin");
    }
    for (const el of volumeFills) el.style.removeProperty("width");
    for (const el of volumeHandles) el.style.removeProperty("left");
    for (const clip of qa("volume-clip")) {
      clip.style.removeProperty("width");
      clip.style.removeProperty("overflow");
      clip.style.removeProperty("transition");
    }
    for (const el of [...timeCurrents, ...timeTotals, ...tooltips]) {
      el.style.removeProperty("font-variant-numeric");
    }
    if (settingsMenu) settingsMenu.style.removeProperty("display");
    [...playIcons, ...pauseIcons, ...volumeOns, ...volumeOffs, ...fullscreenEnters, ...fullscreenExits]
      .forEach((el) => el.style.removeProperty("display"));
  });
}
